import React, { useState, useEffect } from 'react';
import { ShieldCheck, Search } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import ReputationScoreCard from '../../../components/admin/crm/ReputationScoreCard';
import ReputationFactors from '../../../components/admin/crm/ReputationFactors';
import ReputationInsights from '../../../components/admin/crm/ReputationInsights';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientReputation = () => {
  const [entityType, setEntityType] = useState('client');
  const [entityId, setEntityId] = useState('');
  const [entities, setEntities] = useState([]);
  const [reputation, setReputation] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchEntities();
  }, [entityType]);

  const fetchEntities = async () => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'client':
          endpoint = '/api/crm/clients';
          break;
        case 'lead':
          endpoint = '/api/crm/leads';
          break;
        case 'contact':
          endpoint = '/api/crm/contacts';
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch entities');

      const data = await response.json();
      setEntities(data);

      // Auto-select first entity
      if (data.length > 0 && !entityId) {
        setEntityId(data[0].id);
        fetchReputation(entityType, data[0].id);
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load entities',
        variant: 'destructive'
      });
    }
  };

  const fetchReputation = async (type, id) => {
    if (!id) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${API_URL}/api/crm/reputation/${type}/${id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch reputation');

      const data = await response.json();
      setReputation(data);
    } catch (error) {
      console.error('Error fetching reputation:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reputation data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEntityChange = (id) => {
    setEntityId(id);
    fetchReputation(entityType, id);
  };

  const handleEntityTypeChange = (type) => {
    setEntityType(type);
    setEntityId('');
    setReputation(null);
  };

  const getEntityLabel = (entity) => {
    if (entityType === 'client' || entityType === 'lead') {
      return entity.company_name || entity.name || 'Unknown';
    } else if (entityType === 'contact') {
      return entity.name || 'Unknown';
    }
    return 'Unknown';
  };

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
            <ShieldCheck className="h-6 w-6 text-[#00B4D8]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42]">Client Reputation</h2>
            <p className="text-sm text-[#6C757D]">Analyze reputation scores and risk indicators</p>
          </div>
        </div>
      </div>

      {/* Entity Selection */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Entity Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity Type
              </label>
              <select
                value={entityType}
                onChange={(e) => handleEntityTypeChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              >
                <option value="client">Client</option>
                <option value="lead">Lead</option>
                <option value="contact">Contact</option>
              </select>
            </div>

            {/* Entity Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
              </label>
              <select
                value={entityId}
                onChange={(e) => handleEntityChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              >
                <option value="">Select {entityType}...</option>
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {getEntityLabel(entity)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reputation Data */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading reputation data...</p>
          </CardContent>
        </Card>
      ) : reputation ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Score Card */}
          <div>
            <ReputationScoreCard
              score={reputation.score}
              health={reputation.health}
            />
          </div>

          {/* Right Column - Insights and Factors */}
          <div className="lg:col-span-2 space-y-6">
            {/* Insights */}
            <ReputationInsights
              health={reputation.health}
              risk={reputation.risk}
              engagement={reputation.engagement}
              activityLevel={reputation.activity_level}
            />

            {/* Factors */}
            <ReputationFactors factors={reputation.factors} />
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              Select an entity to view reputation data
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientReputation;
